import {makeAutoObservable, observable} from 'mobx';

import {indexOfId} from '@/domain/models/Model';
import {Reaction} from '@/domain/models/Reaction';
import queryClient from '@/shared/lib/react-query/queryClient';

import {FEATURES_QUERY_REACTIONS} from '../consts/queryKeys';
import type {LastViewport} from './types';

class ReactionsStore {
  list = observable.array<Reaction>([], {deep: false});
  count = 0;
  initialReaction: Reaction | undefined;
  fetchedAllReactions: boolean = false;
  private _shouldRefresh: boolean = false;
  private _lastViewport: LastViewport | undefined;

  constructor() {
    makeAutoObservable(this, {
      lastViewport: false,
    });
  }

  setLastViewport(value: LastViewport) {
    this._lastViewport = value;
  }

  clearLastViewport() {
    this._lastViewport = undefined;
  }

  get lastViewport() {
    return this._lastViewport;
  }

  get lastLoadedReactionAt() {
    return this.list[this.list.length - 1]?.created_at;
  }

  get shouldRefresh() {
    return this._shouldRefresh;
  }

  setShouldRefresh(shouldRefresh: boolean) {
    this._shouldRefresh = shouldRefresh;
  }

  get lastReaction(): Reaction | undefined {
    return this.list[0] ?? this.initialReaction;
  }

  get lastThreeReactions() {
    const lastThree = this.list.slice(0, 3);
    if (lastThree.length === 0 && this.lastReaction) {
      lastThree.push(this.lastReaction);
    }

    return lastThree;
  }

  get empty() {
    return this.list.length === 0 && this.fetchedAllReactions;
  }

  setFetchedAllReactions(fetched: boolean) {
    this.fetchedAllReactions = fetched;
  }

  private get reactionsIds() {
    return new Set(this.list.map((item) => item.id));
  }

  addOrReplaceReaction(reaction: Reaction) {
    const index = this.list.findIndex((item) => item.id === reaction.id);

    if (index >= 0) {
      this.list[index] = reaction;
      return;
    }

    this.list.unshift(reaction);
    this.count += 1;
  }

  addReactions(list: Reaction[]) {
    if (list.length === 0) {
      return;
    }

    const nextItems = list.filter((item) => !this.reactionsIds.has(item.id));

    if (nextItems.length > 0) {
      this.list.push(...nextItems);
    }
  }

  applyLatestReactions(list: Reaction[]): boolean {
    const topReaction = this.list[0];

    if (topReaction) {
      const topReactionIndex = indexOfId(topReaction.id, list);

      if (topReactionIndex >= 0) {
        this.list.unshift(...list.slice(0, topReactionIndex));
        return true;
      }
    }

    this.list.replace(list);
    this.fetchedAllReactions = false;
    this.clearLastViewport();

    return false;
  }

  setCount(count: number) {
    this.count = count;
  }

  setInitialReaction(initialReaction?: Reaction) {
    this.initialReaction = initialReaction;
  }

  removeByUser(userId: number) {
    const nextList = this.list.filter(
      (reaction) => reaction.user_id !== userId
    );
    const removedCount = this.list.length - nextList.length;

    if (removedCount === 0) {
      return;
    }

    this.list.replace(nextList);
    this.count = Math.max(0, this.count - removedCount);
  }

  remove(reactionId: number) {
    this.list.replace(
      this.list.filter((reaction) => reaction.id !== reactionId)
    );
  }

  resetList() {
    this.list.clear();
    this.fetchedAllReactions = false;
    this.dropCache();
    this.clearLastViewport();
  }

  dropCache() {
    queryClient.removeQueries({
      queryKey: [FEATURES_QUERY_REACTIONS],
    });
  }
}

export const reactionsStore = new ReactionsStore();
